import { VideoLectureTypeEnum } from '@crehana/compass.knowledgehub/types';

import type { TCourse } from '../../types';
import trailerTypeToMimeType from '../../utils/trailerTypeToMimeType';
import type { LearningUserCourseDetailQueryQuery } from './graphql/org-course-detail-collaborator-v2-query.v5.generated';

type TContentBase = NonNullable<
  LearningUserCourseDetailQueryQuery['knowledge_hub_collaborator_v2']['get_collaborator_content_detail']
>;

const VIDEO_TYPE_MAP: Readonly<Record<string, VideoLectureTypeEnum>> = {
  quiz: VideoLectureTypeEnum.VideoLectureQuiz,
  class: VideoLectureTypeEnum.VideoLectureClass,
  break: VideoLectureTypeEnum.VideoLectureBreak,
  exercise: VideoLectureTypeEnum.VideoLectureExercise,
  scorm: VideoLectureTypeEnum.VideoLectureScorm,
  pdf: VideoLectureTypeEnum.VideoLecturePdf,
  iframe: VideoLectureTypeEnum.VideoLectureIframe,
  survey: VideoLectureTypeEnum.VideoLectureSurvey,
  live_session: VideoLectureTypeEnum.VideoLectureLiveSession,
};

export const mapCollaboratorV2ToCourse = (
  contentBase: TContentBase,
): TCourse | undefined => {
  const { course } = contentBase;

  if (!contentBase.original_id || !course) {
    return undefined;
  }

  const totalLessons =
    course.modules.reduce((acc, module) => acc + module.videos.length, 0) || 0;

  return {
    originalId: contentBase.original_id,
    title: course.title || '',
    description: course.description || '',
    professorName: '',
    professor: course.professor
      ? {
          fullName: course.professor.full_name,
          occupation: course.professor.occupation || '',
          profilePictureUrl: course.professor.profile_picture_url?.url || '',
        }
      : undefined,
    metaTitle: course.title || '',
    metaDescription: course.description || '',
    url: contentBase.enrollment_url || '',
    promoImage: course.image || '',
    duration: course.duration || 0,
    totalLessons,
    totalAttachments: course.total_attachments_number,
    enrollments: 0,
    modifiedDate: course.modified_date || '',
    isMandatory: course.is_mandatory ?? null,
    level: course.course_level ? { name: course.course_level.name } : undefined,
    courseModules: course.modules.map(module => ({
      id: module.id,
      originalId: module.id,
      order: module.order,
      name: module.name,
      duration: module.videos.reduce((acc, video) => acc + video.duration, 0),
      videoLectureSet: module.videos.map(video => ({
        id: video.id,
        originalId: video.id,
        order: video.order,
        title: video.title,
        duration: video.duration,
        videoTypeEnum:
          VIDEO_TYPE_MAP[video.video_type || ''] ||
          VideoLectureTypeEnum.VideoLectureClass,
      })),
    })),
    videoTrailer: course.video_trailer?.video_url
      ? {
          sources: [
            {
              type: trailerTypeToMimeType(
                course.video_trailer.video_source_type,
              ),
              src: course.video_trailer.video_url,
            },
          ],
        }
      : undefined,
    requirements:
      course.requirements?.map(req => ({
        __typename: 'CourseRequirementNode' as const,
        text: req.text ?? null,
        position: req.position ?? null,
        requirement_details:
          req.requirement_details?.map(detail => ({
            __typename: 'CourseRequirementDetailNode' as const,
            title: detail.title ?? null,
            text: detail.text ?? null,
            position: detail.position ?? null,
          })) ?? null,
      })) ?? null,
  } satisfies TCourse;
};
